import { z } from 'zod'

import { ServiceFeeType } from '@/travelsuit/fees.types'
import { WorldlineOrderStatus } from '@/travelsuit/worldline'
import { UserNewZ } from '@/types/userNew'

import { UserCurrencyZ } from './common'
import { TripStatus } from './trip'

export const FeeIdZ = z.number().brand<'FeeId'>()

export type FeeId = z.infer<typeof FeeIdZ>

const ActiveUserFeeReportDetailZ = z.object({
	user: UserNewZ.pick({ id: true, first_name: true, last_name: true, middle_name: true }),
	trips: z.array(
		z.object({
			name: z.string(),
			booked_date: z.string(),
			trip_id: z.number(),
			status: z.enum(TripStatus),
			approved_date: z.string().nullable(),
		}),
	),
	total_price: z.number(),
	currency: UserCurrencyZ,
	refund_dt: z.string().nullable(),
	status: z.enum(WorldlineOrderStatus).nullable(),
	expense_reports: z.array(
		z.object({
			name: z.string(),
			expense_report_id: z.number(),
			approval_dt: z.string().datetime(),
			start_date: z.iso.date().nullable(),
			end_date: z.iso.date().nullable(),
		}),
	),
})

export type ActiveUserFeeReportDetail = z.infer<typeof ActiveUserFeeReportDetailZ>

export const ActiveUserFeeDetailsZ = z.array(ActiveUserFeeReportDetailZ)

export type ActiveUserFeeDetails = z.infer<typeof ActiveUserFeeDetailsZ>

const FeeServiceDetailsZ = z.object({
	id: FeeIdZ,
	status: z.enum(WorldlineOrderStatus).nullable(),
	total_price: z.number(),
})

const VpaFeeServiceDetailsZ = FeeServiceDetailsZ.extend({
	bs_info: z
		.object({
			service_name: z.string(),
			service_dates: z.string(),
		})
		.nullable(),
})

const TripFeeZ = z.object({
	service_type: z.literal(ServiceFeeType.Trip),
	[ServiceFeeType.Trip]: FeeServiceDetailsZ,
})

const VpaFeeZ = z.object({
	service_type: z.literal(ServiceFeeType.VirtualPaymentAutomation),
	[ServiceFeeType.VirtualPaymentAutomation]: VpaFeeServiceDetailsZ,
})

const SubscriptionFeeZ = z.object({
	service_type: z.literal(ServiceFeeType.SubscriptionPayment),
	[ServiceFeeType.SubscriptionPayment]: FeeServiceDetailsZ,
})

const ActiveUserFeeZ = z.object({
	service_type: z.literal(ServiceFeeType.ActiveUser),
	[ServiceFeeType.ActiveUser]: FeeServiceDetailsZ,
})

export const FeeZ = z.union([TripFeeZ, VpaFeeZ, SubscriptionFeeZ, ActiveUserFeeZ])

export type Fee = z.infer<typeof FeeZ>
